
import Tender from '~/app/Tender';
import { tenderPhases, tenderTypes, values } from '~/assets/mappings.json';
const supportedLanguages = [
  'nld',
  'eng',
  'fre',
  'fra',
  'deu',
  'slv',
  'hun',
  'bul',
  'rom',
  'ell',
  'ces',
  'dan',
  'est',
  'rus',
  'ita',
  'slk',
  'lit',
  'pol',
  'por',
  'swe'
];

export default {
  props: {
    tender: {
      type: Object,
      required: true
    }
  },

  data () {
    let currentLotNumber = null;
    if ('lot' in this.$route.query) {
      currentLotNumber = this.$route.query.lot;
    }

    currentLotNumber = currentLotNumber ?? [...this.tender.tenderVersions[0].lots].sort((a, b) =>
      a.lotNumber.localeCompare(b.lotNumber)
    )[0].lotNumber;
    return {
      currentLotNumber,
      tenderPhases,
      tenderTypes,
      addToMyTendersLoading: false,
      similarTenders: [],
      competition: [],
      files: [],
      matches: [],
      share: {
        show: false
      },
      values,
      signup: {
        modal: false,
        consultant: undefined
      },

      translationEnabled: true,
      translationData: null,
      translationLoading: false,

      summaryActive: false
    };
  },
  fetchOnServer: true,
  fetchDelay: 0,
  async fetch () {
    const { files } = await this.$axios.$get(`tenderfolder/${this.tender.id}`);

    this.files = files;

    if (files.length === 0 || this.searchWords.length === 0) {
      return;
    }

    if (this.tender.id in this.$store.state.tenderSearchWords.results) {
      this.files = files.map(x => ({
        ...x,
        matches: this.$store.state.tenderSearchWords.results[this.tender.id].find(y => y.documentId === x.id)?.matchedWords ?? []
      }));
      return;
    }

    const params = new URLSearchParams({
      tenderid: this.tender.id
    });

    for (const word of this.searchWords) {
      params.append('searchWords', word.word);
    }

    const { matches } = await this.$axios.$get(
      `/tendersearchwordmatches?${params.toString()}`
    );

    this.matches = matches;
    this.files = files.map(x => ({
      ...x,
      matches: matches?.find(y => y.documentId === x.id)?.matchedWords ?? []
    }));
  },
  computed: {
    searchWords () {
      return this.$store.state.overview.filters.find(
        x => x.name === 'scanner'
      ).options;
    },

    currentLot: {
      get () {
        return this.tender.tenderVersions[0].lots.find(x => x.lotNumber === this.currentLotNumber);
      },

      set (value) {
        this.currentLotNumber = value.lotNumber;
      }
    },
    translation () {
      if (this.translationEnabled) {
        return this.translationData;
      }

      return null;
    },
    language () {
      return this.latestVersion.languages?.[0]?.iso639_2T ?? this.$auth.user.iso6392T;
    },

    allowTranslate () {
      return supportedLanguages.includes(this.language);
    },

    expected () {
      return this.tender.status === 'Expected';
    },

    cpvCodes () {
      const cpvs = [];

      for (const cpv of [...this.latestVersion.cpvs, ...this.currentLot.cpvs]) {
        if (!cpvs.some(c => c.code === cpv.code)) {
          cpvs.push(cpv);
        }
      }

      return cpvs;
    },

    organizations () {
      return [...this.tender.organizations].sort(a => a.isAdvisor);
    },

    value () {
      return Tender.getLotValue(this.tender, this.currentLot);
    },

    description () {
      let description = '';

      if (this.currentLot.description !== null) {
        if (
          this.latestVersion.shortDescription.length >
          this.currentLot.description.length
        ) {
          description = this.latestVersion.shortDescription;
        } else {
          description = this.currentLot.description;
        }
      } else if (this.latestVersion.shortDescription !== null) {
        description = this.latestVersion.shortDescription;
      }

      return description;
    },

    location () {
      return Tender.getLocation(this.tender, this.currentLot);
    },

    lots () {
      return [...this.latestVersion.lots].sort((a, b) =>
        a.lotNumber.localeCompare(b.lotNumber)
      );
    },

    latestVersion () {
      if (this.tender.tenderVersions.length === 0) {
        throw new Error('This tender is missing versions.');
      }

      return this.tender.tenderVersions[0];
    },

    publication () {
      if (
        !this.latestVersion.publications ||
        this.latestVersion.publications.length < 1
      ) {
        return;
      }

      return (
        this.latestVersion.publications.find(x => x.isApplicationPlatform) ??
        this.publication[0]
      );
    },

    tenderPhase () {
      if (!this.publication || this.publication.tenderPhase === 'Unknown') {
        return 'Onbekend';
      }

      return this.publication.tenderPhase;
    },

    sourceURL () {
      if (!this.publication) {
        return;
      }

      return this.publication.publicationUrl;
    },

    source () {
      if (this.publication && this.publication.tenderSource) {
        return this.publication.tenderSource.name;
      }

      return undefined;
    },

    deadline () {
      return this.currentLot.planningItems.find(
        x => x.type === 'ApplicationDeadlineDate'
      );
    },

    extensionDate () {
      return this.currentLot.planningItems.find(x => x.type === 'EndDate');
    },
    hasPrimaryDoc () {
      return this.files.some(doc => doc.isPrimary);
    }
  },

  watch: {
    searchWords () {
      this.$fetch();
    },
    currentLot () {
      this.loadLotTranslation();
    }
  },

  methods: {
    async addToMyTenders (destination) {
      this.addToMyTendersLoading = true;
      const { id } = await this.$axios.$post('/project', {
        lotId: this.currentLot.id
      });

      this.$router.push({
        path: '/collaborate/my-tenders',
        query: { destination },
        hash: id
      });
      this.addToMyTendersLoading = false;
    },

    goBack () {
      if (this.$nuxt.context.from && this.$nuxt.context.from.path === '/') {
        this.$router.back();
        return;
      }

      this.$router.push('/');
    },

    async loadLotTranslation () {
      if (this.lots.length <= 1 || this.currentLot.id in this.translationData.lots) {
        return;
      }

      this.$set(this.translationData.lots, this.currentLot.id, await this.$axios.$get(`/tendertranslation/lot/${this.currentLot.id}?language=${this.$auth.user.iso6392T}`));
    },

    async loadTenderTranslation () {
      this.translationLoading = true;
      try {
        if (!this.translationData) {
          this.translationData = {
            ...await this.$axios.$get(`/tendertranslation/tender/${this.tender.id}?language=${this.$auth.user.iso6392T}`),
            lots: {}
          };
        }

        await this.loadLotTranslation();
      } catch (e) {
        console.error(e);
        this.$toast({ error: true, title: 'Er ging iets mis tijdens het ophalen van de vertaling.' });
      }
      this.translationLoading = false;
    }
  }
};
